import React, { useCallback, useMemo } from "react";
import { MenuItemOptionListProps } from "src/interfaces/menu-items-props.interface";
import { MenuItemOption } from "src/interfaces/menu.interface";

const MenuItemOptionList = React.memo(
  ({ menuItemOptions, onEdit, onDelete }: MenuItemOptionListProps) => {
    const hasMenuItemOptions = useMemo(() => {
      return Array.isArray(menuItemOptions) && menuItemOptions.length > 0;
    }, [menuItemOptions]);

    const handleEditClick = useCallback(
      (menuItemOptionIndex: number) => {
        onEdit(menuItemOptionIndex);
      },
      [onEdit]
    );

    const handleDeleteClick = useCallback(
      (menuItemOption: MenuItemOption, menuItemOptionIndex: number) => {
        onDelete(menuItemOption, menuItemOptionIndex);
      },
      [onDelete]
    );

    if (!hasMenuItemOptions) {
      return (
        <div className="opacity-[50%] cursor-not-allowed pointer-events-none">
          <div className="mt-2 transition-transform hover:shadow-lg flex-col flex justify-between rounded-md border border-[#F1F1F2] py-2 px-4 mb-3 relative">
            <div className="flex gap-x-5 w-full items-center relative">
              <div className="flex justify-between w-full">
                <div className="text-[12px] text-[#9B9B9B] flex items-center">
                  <span>No options</span>
                </div>

                <div className="flex items-center justify-start gap-0.5">
                  <span className="p-2 rounded-full text-[#6B7280] opacity-80">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5" aria-hidden>
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </span>
                  <span className="p-2 rounded-full text-[#6B7280] opacity-80">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5" aria-hidden>
                      <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.518.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        {menuItemOptions.map((menuItemOption, menuItemOptionIndex) => (
          <div
            className="mt-2 transition-transform hover:shadow-lg flex-col flex justify-between rounded-md border border-[#F1F1F2] p-4 mb-3 relative"
            key={menuItemOption.correlationId}
          >
            <div className="flex gap-x-5 w-full items-center relative">
              <div className="flex-1 flex items-center gap-x-2 min-w-0">
                <div className="text-xs font-medium text-[#252525] flex items-center flex-shrink-0">
                  <span>{menuItemOption?.name}</span>
                </div>
                {menuItemOption?.description && (
                  <div className="text-xs text-[#9B9B9B] flex items-center min-w-0">
                    <span className="truncate">{menuItemOption.description}</span>
                  </div>
                )}
              </div>
              <div className="absolute right-0 flex items-center justify-start gap-0.5">
                <button
                  type="button"
                  onClick={() => handleEditClick(menuItemOptionIndex)}
                  className="p-2 rounded-full text-[#6B7280] opacity-80 hover:opacity-100 hover:bg-[#f3f4f6] hover:text-[#111827] transition-colors"
                  aria-label="Edit option"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-5 h-5"
                  >
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    handleDeleteClick(menuItemOption, menuItemOptionIndex)
                  }
                  className="p-2 rounded-full text-[#6B7280] opacity-80 hover:opacity-100 hover:bg-red-50 hover:text-[#DC2626] transition-colors"
                  aria-label="Delete option"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.518.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </>
    );
  }
);

MenuItemOptionList.displayName = "MenuItemOptionList";

export default MenuItemOptionList;
